/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateEvent } from '../slice/eventSlice';
import { FaTimes, FaUpload, FaTrash, FaCheckCircle, FaPlus, FaCalendarAlt, FaImages, FaGlobe } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import './EditEvent.css';
import { validateImageFile } from '../utils/fileValidation';

const EditEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'images', 'seo'

  // Form State
  const [basicInfo, setBasicInfo] = useState({
    title: '',
    short_description: '',
    status: 1,
    sort: 0
  });

  const [slides, setSlides] = useState([]);
  const [imagesStatus, setImagesStatus] = useState(1);

  const [seo, setSeo] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    status: 1
  });

  const fileInputRefs = useRef({});

  // Fetch event details
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const res = await axiosInstance.get(`/events/${id}`);
        const data = res.data;
        if (data) {
          setBasicInfo({
            title: data.title || '',
            short_description: data.short_description || '',
            status: data.status !== undefined ? data.status : 1,
            sort: data.sort !== undefined ? data.sort : 0
          });
          setSlides(data.slides || []);
          // In database, we can check section status or default to 1
          setImagesStatus(1);
          setSeo({
            meta_title: data.meta_title || '',
            meta_description: data.meta_description || '',
            meta_keywords: data.meta_keywords || '',
            status: 1
          });
        }
      } catch (err) {
        console.error('Failed to fetch event details', err);
        toast.error('Failed to load event details');
      }
    };

    fetchEventData();
  }, [id]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSeoChange = (e) => {
    const { name, value } = e.target;
    setSeo((prev) => ({ ...prev, [name]: value }));
  };

  // Slides Management
  const handleAddSlide = () => {
    const newSlide = {
      id: Date.now().toString(),
      type: 'image',
      heading: '',
      description: '',
      image: '',
      video: '',
      alt: '',
      status: 1,
      sort: (slides.length + 1).toString(),
      thumb: slides.length === 0 ? 1 : 0 // Set as thumbnail if first slide
    };
    setSlides([...slides, newSlide]);
  };

  const handleRemoveSlide = (slideId) => {
    const updated = slides.filter((s) => s.id !== slideId);
    // If we deleted the thumbnail, set the first remaining slide as thumbnail
    if (slides.find((s) => s.id === slideId)?.thumb === 1 && updated.length > 0) {
      updated[0].thumb = 1;
    }
    setSlides(updated);
  };

  const handleSlideChange = (slideId, field, value) => {
    setSlides(
      slides.map((s) => {
        if (s.id === slideId) {
          let updated = { ...s, [field]: value };
          if (field === 'thumb' && value === 1) {
            // Reset other slides thumbnail
            setSlides(slides.map((oth) => (oth.id === slideId ? { ...oth, thumb: 1 } : { ...oth, thumb: 0 })));
            return updated;
          }
          return updated;
        }
        return s;
      })
    );
  };

  const handleImageUpload = async (slideId, e) => {
    const file = e.target.files[0];
    if (!validateImageFile(file, e)) return;
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('image', file);

    try {
      toast.info('Uploading image...');
      const res = await axiosInstance.post('/products/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const filename = res.data.filename;
      handleSlideChange(slideId, 'image', filename);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload failed', error);
      toast.error('Image upload failed');
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    const backendHost = process.env.REACT_APP_BACKEND
      ? process.env.REACT_APP_BACKEND.replace(/\/$/, '')
      : (process.env.REACT_APP_BACKEND_URL || '').split('/api')[0] || 'http://localhost:8000';
    return `${backendHost}/uploads/${image}`;
  };

  // Extract YouTube video ID from any YouTube URL format and return thumbnail
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    // Handles: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /[?&]v=([a-zA-Z0-9_-]{11})/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
      }
    }
    return null; // Not a recognized YouTube URL
  };

  const handleSave = () => {
    // Validation
    if (!basicInfo.title || !basicInfo.short_description) {
      toast.error('Please enter Title and Short Description in Basic Info tab');
      return;
    }

    if (basicInfo.short_description.length < 100 || basicInfo.short_description.length > 750) {
      toast.error('Short Description must be between 100 and 750 characters');
      return;
    }

    // Verify at least one slide exists and has URL if images tab is enabled
    if (imagesStatus === 1 && slides.length === 0) {
      toast.error('Please add at least one slide in Event Images tab');
      return;
    }

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (slide.type === 'image' && !slide.image) {
        toast.error(`Please choose an image for Slide ${i + 1}`);
        return;
      }
      if (slide.type === 'video' && !slide.video) {
        toast.error(`Please enter video URL for Slide ${i + 1}`);
        return;
      }
    }

    const payload = {
      title: basicInfo.title,
      short_description: basicInfo.short_description,
      status: basicInfo.status,
      sort: basicInfo.sort,
      slides: slides,
      meta_title: seo.meta_title,
      meta_description: seo.meta_description,
      meta_keywords: seo.meta_keywords
    };

    dispatch(updateEvent({ id: parseInt(id), data: payload }));
    navigate('/event-list');
  };

  return (
    <div className="edit-event-container animate-fade-up">
      <div className="edit-event-card">
        {/* Header */}
        <div className="edit-event-header">
          <div className="header-title-group">
            <span className="back-link" onClick={() => navigate('/event-list')}>
              Event Manager
            </span>
            <span className="separator">/</span>
            <h2>Modify Event</h2>
          </div>
          <button className="close-btn" onClick={() => navigate('/event-list')}>
            <FaTimes />
          </button>
        </div>

        {/* Tab Layout Container */}
        <div className="edit-event-tabs-wrapper">
          {/* Left Tab Sidebar */}
          <div className="edit-event-sidebar">
            <div className={`sidebar-tab-item ${activeTab === 'basic' ? 'active' : ''}`} onClick={() => setActiveTab('basic')}>
              <div className="tab-label-group">
                <FaCalendarAlt className="tab-icon" />
                <span>Basic Info</span>
              </div>
              <div className="tab-status-group">
                <FaCheckCircle className="tab-checkmark active" />
                <label className="toggle-switch-tab disabled">
                  <input type="checkbox" checked={basicInfo.status === 1} readOnly />
                  <span className="toggle-slider-tab"></span>
                </label>
              </div>
            </div>

            <div className={`sidebar-tab-item ${activeTab === 'images' ? 'active' : ''}`} onClick={() => setActiveTab('images')}>
              <div className="tab-label-group">
                <FaImages className="tab-icon" />
                <span>Event Images</span>
              </div>
              <div className="tab-status-group">
                <FaCheckCircle className={`tab-checkmark ${slides.length > 0 ? 'active' : ''}`} />
                <label className="toggle-switch-tab" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={imagesStatus === 1} onChange={(e) => setImagesStatus(e.target.checked ? 1 : 0)} />
                  <span className="toggle-slider-tab"></span>
                </label>
              </div>
            </div>

            <div className={`sidebar-tab-item ${activeTab === 'seo' ? 'active' : ''}`} onClick={() => setActiveTab('seo')}>
              <div className="tab-label-group">
                <FaGlobe className="tab-icon" />
                <span>SEO Manager</span>
              </div>
              <div className="tab-status-group">
                <FaCheckCircle className={`tab-checkmark ${seo.meta_title ? 'active' : ''}`} />
                <label className="toggle-switch-tab" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={seo.status === 1}
                    onChange={(e) => setSeo((prev) => ({ ...prev, status: e.target.checked ? 1 : 0 }))}
                  />
                  <span className="toggle-slider-tab"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Right Content Panel */}
          <div className="edit-event-panel">
            {/* 1. Basic Info Tab Panel */}
            {activeTab === 'basic' && (
              <div className="panel-tab-content">
                <h3 className="panel-title">Basic Info</h3>
                <div className="premium-form-full">
                  <div className="form-group">
                    <label>
                      Event Title <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={basicInfo.title}
                      onChange={handleBasicChange}
                      placeholder="Event Title"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Short Description <span className="required">*</span>
                    </label>
                    <textarea
                      name="short_description"
                      value={basicInfo.short_description}
                      onChange={handleBasicChange}
                      placeholder="Short Description"
                      rows="6"
                      required
                    />
                    <div className="field-subtext-row">
                      <small className="char-counter">{basicInfo.short_description.length} / 750 Chars</small>
                    </div>
                    <small className="field-subtext note">Note: Min length: 100 Characters | Max length: 750 Characters</small>
                  </div>
                </div>
              </div>
            )}

            {/* 2. Event Images Tab Panel */}
            {activeTab === 'images' && (
              <div className="panel-tab-content">
                <div className="panel-header-row">
                  <h3 className="panel-title">Event Images</h3>
                  <button type="button" className="btn-add-slide" onClick={handleAddSlide}>
                    <FaPlus style={{ marginRight: '6px' }} /> Add Slide
                  </button>
                </div>

                {slides.length === 0 ? (
                  <div className="empty-slides-placeholder">No slides added. Click &quot;+ Add Slide&quot; to add event images/videos.</div>
                ) : (
                  <div className="slides-container-list">
                    {slides.map((slide, index) => (
                      <div key={slide.id} className="slide-card-item">
                        {/* Slide Card Header */}
                        <div className="slide-card-header">
                          <h4>Slide {index + 1}</h4>
                          <div className="slide-header-actions">
                            <label className="toggle-switch-tab">
                              <input
                                type="checkbox"
                                checked={slide.status === 1}
                                onChange={(e) => handleSlideChange(slide.id, 'status', e.target.checked ? 1 : 0)}
                              />
                              <span className="toggle-slider-tab"></span>
                            </label>
                            <span className="status-text-slide">{slide.status === 1 ? 'Active' : 'Inactive'}</span>
                            <button type="button" className="btn-slide-delete" onClick={() => handleRemoveSlide(slide.id)}>
                              <FaTrash />
                            </button>
                          </div>
                        </div>

                        {/* Slide Card Form */}
                        <div className="slide-card-body">
                          <div className="slide-fields-column">
                            <div className="form-group-slide">
                              <label>
                                Banner Type <span className="required">*</span>
                              </label>
                              <select value={slide.type} onChange={(e) => handleSlideChange(slide.id, 'type', e.target.value)}>
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                              </select>
                            </div>

                            <div className="form-group-slide" style={{ marginTop: '1rem' }}>
                              <label>
                                URL <span className="required">*</span>
                              </label>
                              {slide.type === 'image' ? (
                                <div className="file-uploader-box">
                                  <button
                                    type="button"
                                    className="btn-file-select"
                                    onClick={() => fileInputRefs.current[slide.id]?.click()}
                                  >
                                    <FaUpload style={{ marginRight: '8px' }} /> Choose file
                                  </button>
                                  <span className="file-chosen-text">{slide.image ? slide.image : 'No file chosen'}</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    ref={(el) => (fileInputRefs.current[slide.id] = el)}
                                    onChange={(e) => handleImageUpload(slide.id, e)}
                                  />
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  value={slide.video}
                                  onChange={(e) => handleSlideChange(slide.id, 'video', e.target.value)}
                                  placeholder="e.g. https://www.youtube.com/watch?v=..."
                                />
                              )}
                            </div>

                            <div className="thumbnail-checkbox-row" style={{ marginTop: '1.25rem' }}>
                              <input
                                type="checkbox"
                                id={`thumb-chk-${slide.id}`}
                                checked={slide.thumb === 1}
                                onChange={(e) => handleSlideChange(slide.id, 'thumb', e.target.checked ? 1 : 0)}
                              />
                              <label htmlFor={`thumb-chk-${slide.id}`} style={{ cursor: 'pointer', userSelect: 'none' }}>
                                Set as Thumbnail
                              </label>
                            </div>
                          </div>

                          {/* Preview Column */}
                          <div className="slide-preview-column">
                            <label>Preview</label>
                            <div
                              className="slide-preview-display"
                              style={{
                                width: '100%',
                                maxWidth: '220px',
                                height: '110px',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#0c0c0b',
                                border: '1px dashed rgba(196, 168, 130, 0.3)',
                                borderRadius: '6px',
                                boxSizing: 'border-box',
                                position: 'relative',
                                zIndex: 1
                              }}
                            >
                              {slide.type === 'image' && slide.image ? (
                                <img
                                  src={getImageUrl(slide.image)}
                                  alt="Preview"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    display: 'block',
                                    borderRadius: '4px'
                                  }}
                                />
                              ) : slide.type === 'video' && slide.video ? (
                                (() => {
                                  const ytThumb = getYouTubeThumbnail(slide.video);
                                  return ytThumb ? (
                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                      <img
                                        src={ytThumb}
                                        alt="Video Preview"
                                        style={{
                                          maxWidth: '100%',
                                          maxHeight: '100%',
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          display: 'block',
                                          borderRadius: '4px'
                                        }}
                                        onError={(e) => {
                                          // fallback to mqdefault if hqdefault not available
                                          const vid = slide.video.match(/[a-zA-Z0-9_-]{11}/)?.[0];
                                          if (vid) e.target.src = `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
                                        }}
                                      />
                                      {/* Play icon overlay */}
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%)',
                                          background: 'rgba(0,0,0,0.6)',
                                          borderRadius: '50%',
                                          width: '32px',
                                          height: '32px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          pointerEvents: 'none'
                                        }}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                                          <path d="M8 5v14l11-7z" />
                                        </svg>
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                                      <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🎬</div>
                                      <span style={{ fontSize: '0.7rem', color: '#c4a882', wordBreak: 'break-all' }}>Video URL Set</span>
                                    </div>
                                  );
                                })()
                              ) : (
                                <div
                                  className="no-preview-placeholder"
                                  style={{ fontSize: '0.85rem', color: '#a0a0a0', fontStyle: 'italic' }}
                                >
                                  No Preview
                                </div>
                              )}
                            </div>

                            <div className="form-group-slide" style={{ marginTop: '1rem', width: '100%' }}>
                              <label>
                                Alt Text <span className="required">*</span>
                              </label>
                              <input
                                type="text"
                                value={slide.alt}
                                onChange={(e) => handleSlideChange(slide.id, 'alt', e.target.value)}
                                placeholder="Alt Text"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Dynamic Premium Animated "Add More" Card */}
                    <div className="add-more-slide-card-btn" onClick={handleAddSlide}>
                      <div className="add-more-card-content">
                        <FaPlus className="add-more-icon" />
                        <span>Add More Slide</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 3. SEO Manager Tab Panel */}
            {activeTab === 'seo' && (
              <div className="panel-tab-content">
                <h3 className="panel-title">SEO Manager</h3>
                <div className="premium-form-full">
                  <div className="form-group">
                    <label>
                      Meta Title <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="meta_title"
                      value={seo.meta_title}
                      onChange={handleSeoChange}
                      placeholder="Meta Title"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Meta Description <span className="required">*</span>
                    </label>
                    <textarea
                      name="meta_description"
                      value={seo.meta_description}
                      onChange={handleSeoChange}
                      placeholder="Meta Description"
                      rows="5"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Keywords</label>
                    <input
                      type="text"
                      name="meta_keywords"
                      value={seo.meta_keywords}
                      onChange={handleSeoChange}
                      placeholder="Enter keywords..."
                    />
                    <small className="field-subtext">Add commas to separate keywords</small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="edit-event-footer">
          <button type="button" className="btn-premium-outline" onClick={() => navigate('/event-list')}>
            Cancel
          </button>
          <button type="button" className="btn-premium" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEvent;
